import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { Control } from 'react-hook-form';
import { useFormState, useWatch } from 'react-hook-form';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublishIcon from '@mui/icons-material/Publish';
import SaveIcon from '@mui/icons-material/Save';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';

import {
  CONTENT_TAG_STATUS,
  CONTENT_TAG_STATUS_COLORS,
  CONTENT_TAG_STATUS_LABELS,
  type ContentTagStatus,
  type UpdateComponentTagStatusParams,
  type UpdateComponentTagValuesParams,
  useContentComponentTags,
  useUpdateContentComponentTags,
} from '../hooks';

import TagContentForm, { DEFAULT_COMPONENT_TAG_FORM_STATE, type TagContentFormData } from './TagContentForm';

const AccordionSummaryStyled = styled(AccordionSummary)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

const AccordionDetailsStyled = styled(AccordionDetails)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

const AccordionStyled = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&:not(:last-child)': {
    marginBottom: theme.spacing(1),
  },
}));

type Props = {
  tagName: string;
  control: Control<TagContentFormData>;
  onReset?: (data: TagContentFormData) => void;
};

function getStatusLabel(status: ContentTagStatus | undefined): string {
  if (!status) {
    return CONTENT_TAG_STATUS_LABELS[CONTENT_TAG_STATUS.DRAFT];
  }
  return CONTENT_TAG_STATUS_LABELS[status] ?? CONTENT_TAG_STATUS_LABELS[CONTENT_TAG_STATUS.DRAFT];
}

function getStatusColor(status: ContentTagStatus | undefined): string {
  if (!status) {
    return CONTENT_TAG_STATUS_COLORS[CONTENT_TAG_STATUS.DRAFT];
  }
  return CONTENT_TAG_STATUS_COLORS[status] ?? CONTENT_TAG_STATUS_COLORS[CONTENT_TAG_STATUS.DRAFT];
}

type MutationAction = 'save' | 'publish' | null;

export default function TagContentUpdate({ tagName, control, onReset }: Props) {
  const appliedRef = useRef(false);
  const [currentAction, setCurrentAction] = useState<MutationAction>(null);

  const formState = useFormState({ control });
  const formData = useWatch({ control });

  const updateMutation = useUpdateContentComponentTags();

  const slugs = {
    offerBanner: `offer-banner-${tagName}`,
    searchBanner: `search-banner-${tagName}`,
    offerPill: `offer-pill-${tagName}`,
    inflowPill: `inflow-pill-${tagName}`,
    searchPill: `search-pill-${tagName}`,
  };

  const { offerBanner, searchBanner, pill, offerPill, inflowPill, searchPill, isLoading, isFetching, errors } =
    useContentComponentTags(slugs, {
      enabled: !!tagName,
    });

  useEffect(() => {
    if (appliedRef.current) {
      return;
    }
    if (isLoading || isFetching) {
      return;
    }

    appliedRef.current = true;

    const newData: TagContentFormData = {
      offerBanner: {
        icon: offerBanner?.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
        title: offerBanner?.title ?? '',
        subtitle: offerBanner?.subtitle ?? '',
        linkText: offerBanner?.linkText ?? '',
        status: offerBanner?.status || CONTENT_TAG_STATUS.DRAFT,
      },
      searchBanner: {
        icon: searchBanner?.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
        title: searchBanner?.title ?? '',
        subtitle: searchBanner?.subtitle ?? '',
        linkText: searchBanner?.linkText ?? '',
        status: searchBanner?.status || CONTENT_TAG_STATUS.DRAFT,
      },
      pill: {
        icon: pill?.icon || DEFAULT_COMPONENT_TAG_FORM_STATE.icon,
        title: pill?.title ?? '',
        subtitle: pill?.subtitle ?? '',
        linkText: pill?.linkText ?? '',
        status: pill?.status || CONTENT_TAG_STATUS.DRAFT,
      },
    };

    if (onReset !== undefined) {
      onReset(newData);
    }
  }, [isLoading, isFetching, offerBanner, searchBanner, pill, onReset]);

  const handleSave = useCallback(async () => {
    setCurrentAction('save');
    try {
      const valuesParams: Array<UpdateComponentTagValuesParams> = [];
      const statusParams: Array<UpdateComponentTagStatusParams> = [];

      const contentTypeMap = [
        { tag: offerBanner, form: formData.offerBanner, referenceTag: offerBanner },
        { tag: searchBanner, form: formData.searchBanner, referenceTag: searchBanner },
        { tag: offerPill, form: formData.pill, referenceTag: offerPill },
        { tag: inflowPill, form: formData.pill, referenceTag: offerPill },
        { tag: searchPill, form: formData.pill, referenceTag: offerPill },
      ];

      for (const { tag, form, referenceTag } of contentTypeMap) {
        if (!tag?.id || !form) {
          continue;
        }

        const valuesChanged =
          form.icon !== referenceTag?.icon ||
          form.title !== referenceTag?.title ||
          (form.subtitle || '') !== (referenceTag?.subtitle || '') ||
          (form.linkText || '') !== (referenceTag?.linkText || '');

        const statusChanged = form.status !== tag.status;

        if (valuesChanged) {
          valuesParams.push({
            id: tag.id,
            icon: form.icon,
            title: form.title,
            subtitle: form.subtitle,
            linkText: form.linkText,
          });

          if (statusChanged) {
            statusParams.push({ id: tag.id, status: form.status });
          }
        } else if (statusChanged) {
          statusParams.push({ id: tag.id, status: form.status });
        }
      }

      if (valuesParams.length > 0 || statusParams.length > 0) {
        await updateMutation.mutateAsync({ values: valuesParams, statuses: statusParams });
        appliedRef.current = false;
      }
    } finally {
      setCurrentAction(null);
    }
  }, [offerBanner, searchBanner, offerPill, inflowPill, searchPill, formData, updateMutation]);

  const isSaveDisabled = !formState.isDirty || updateMutation.isPending;

  const changedContentTags = [offerBanner, searchBanner, offerPill, inflowPill, searchPill].filter(
    (tag) => tag?.status === CONTENT_TAG_STATUS.CHANGED && tag?.id,
  );
  const hasChangedContent = changedContentTags.length > 0;

  const handlePublishAll = useCallback(async () => {
    setCurrentAction('publish');
    try {
      const statusParams: Array<UpdateComponentTagStatusParams> = [];

      for (const tag of changedContentTags) {
        if (tag?.id) {
          statusParams.push({ id: tag.id, status: CONTENT_TAG_STATUS.PUBLISHED });
        }
      }

      if (statusParams.length > 0) {
        await updateMutation.mutateAsync({ values: [], statuses: statusParams });
        appliedRef.current = false;
      }
    } finally {
      setCurrentAction(null);
    }
  }, [changedContentTags, updateMutation]);

  const offerBannerStatus = offerBanner?.status;
  const searchBannerStatus = searchBanner?.status;
  const pillStatus = pill?.status;

  if (isLoading || isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!offerBanner && !searchBanner && !pill) {
    console.error('Failed to load content tag', { offerBanner, searchBanner, pill });
    return null;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" spacing={1} mb={2}>
        {hasChangedContent && (
          <Tooltip title="Publish all content with 'Changed' status">
            <Button
              variant="outlined"
              color="primary"
              startIcon={currentAction === 'publish' ? <CircularProgress size={20} color="inherit" /> : <PublishIcon />}
              disabled={currentAction !== null}
              onClick={handlePublishAll}
            >
              Publish
            </Button>
          </Tooltip>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={currentAction === 'save' ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          disabled={isSaveDisabled || currentAction !== null}
          onClick={handleSave}
        >
          Save
        </Button>
      </Stack>
      <AccordionStyled>
        <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">Offer Banner</Typography>
            <Chip
              label={getStatusLabel(offerBannerStatus)}
              size="small"
              sx={{ bgcolor: getStatusColor(offerBannerStatus), color: 'white' }}
            />
          </Stack>
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          {errors.offerBanner ? (
            <Alert severity="error">{errors.offerBanner.message ?? 'Failed to load content tag'}</Alert>
          ) : (
            <TagContentForm control={control} namePrefix="offerBanner" />
          )}
        </AccordionDetailsStyled>
      </AccordionStyled>
      <AccordionStyled>
        <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">Search Banner</Typography>
            <Chip
              label={getStatusLabel(searchBannerStatus)}
              size="small"
              sx={{ bgcolor: getStatusColor(searchBannerStatus), color: 'white' }}
            />
          </Stack>
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          {errors.searchBanner ? (
            <Alert severity="error">{errors.searchBanner.message ?? 'Failed to load content tag'}</Alert>
          ) : (
            <TagContentForm control={control} namePrefix="searchBanner" showLinkText />
          )}
        </AccordionDetailsStyled>
      </AccordionStyled>
      <AccordionStyled>
        <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">Pill (offer-pill, inflow-pill, search-pill)</Typography>
            <Chip
              label={getStatusLabel(pillStatus)}
              size="small"
              sx={{ bgcolor: getStatusColor(pillStatus), color: 'white' }}
            />
          </Stack>
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          {errors.pill ? (
            <Alert severity="error">{errors.pill.message ?? 'Failed to load content tag'}</Alert>
          ) : (
            <TagContentForm control={control} namePrefix="pill" />
          )}
        </AccordionDetailsStyled>
      </AccordionStyled>
    </Box>
  );
}
